import { useQuery } from "@apollo/client";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { compact, debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TopCollaborator,
  useTopCollaborators,
} from "../../chat-plus-calendar/util/TopCollaboratorsContext";
import { EventCardAttendee } from "../../event-card/types";
import { useOrgId } from "../../hooks/useOrgId";
import { useSetAIError } from "../ai-chat/hooks/AIErrorContext";
import { SearchPersonsChatQueryDocument } from "../apollo/__generated__/SearchPersonsChat.generated";

type UseSearchPersonsChatResult = {
  searchPersons: EventCardAttendee[];
  handlePersonSearch: (searchQuery: string, hiddenCalendars: string[]) => void;
  resetSearch: () => void;
  setShowEmptyState: (emptyState: boolean) => void;
};

const convertOrgPersonToEventCardAttendee = (collaborator: TopCollaborator): EventCardAttendee => {
  return {
    primaryEmail: collaborator?.calendarId || "",
    primaryCalendar: collaborator?.calendarId || "",
    id: collaborator?.orgPerson?.id || "",
    userId: null,
    isYou: false, // will always be false
    profile: collaborator?.orgPerson?.profile
      ? {
          givenName: collaborator?.orgPerson?.profile?.givenName || null,
          familyName: collaborator?.orgPerson?.profile?.familyName || null,
          externalImageUrl: collaborator?.orgPerson?.profile?.externalImageUrl || null,
        }
      : null,
  };
};

export const useSearchPersonsChat = ({
  searchPersonLimit = 5,
  skip,
  showAllTopCollaborators = false,
}: {
  searchPersonLimit?: number;
  skip?: boolean;
  showAllTopCollaborators?: boolean;
}): UseSearchPersonsChatResult => {
  const { topCollaborators } = useTopCollaborators();

  const { orgId } = useOrgId();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchPersons, setSearchPersons] = useState<EventCardAttendee[]>([]);
  const [hiddenCalendars, setHiddenCalendars] = useState<string[]>([]);
  const [showTopCollaborators, setShowTopCollaborators] = useState<boolean>(false);

  const setError = useSetAIError();

  const { data } = useQuery(SearchPersonsChatQueryDocument, {
    // Limit set to 0 on no search, we dont want anything showing up if the user hasn't typed anything as a mention
    variables: {
      searchQuery,
      limit: searchQuery.trim() ? searchPersonLimit : 0,
      orgRelayId: orgId!,
      excludeCalendars: hiddenCalendars,
    },
    skip: skip || !orgId,
    fetchPolicy: "no-cache",
    onError: (error) => {
      setError({ error, message: "Failed to fetch searchPersons", showUserMessage: false });
    },
  });

  const topCollaboratorsAsEventCardAttendees = useMemo(() => {
    const collaborators: EventCardAttendee[] = [];
    const topCollaboratorsToConvert = showAllTopCollaborators
      ? topCollaborators
      : topCollaborators.slice(0, 4);
    topCollaboratorsToConvert.forEach((collaborator) => {
      if (collaborator) {
        collaborators.push(convertOrgPersonToEventCardAttendee(collaborator));
      }
    });
    return collaborators;
  }, [topCollaborators, showAllTopCollaborators]);

  useEffect(() => {
    if (showTopCollaborators) {
      setSearchPersons(topCollaboratorsAsEventCardAttendees);
    }
  }, [showTopCollaborators, topCollaboratorsAsEventCardAttendees]);

  const setShowEmptyState = useCallback(
    (emptyState: boolean) => {
      setShowTopCollaborators(emptyState);
    },
    [setShowTopCollaborators, showAllTopCollaborators],
  );

  const handlePersonSearch = useMemo(() => {
    const debouncedSearch = debounce((newSearchQuery: string, hiddenCalendars?: string[]) => {
      setSearchQuery(newSearchQuery);
      setHiddenCalendars(hiddenCalendars || []);
    }, 20);

    return (newSearchQuery: string, hiddenCalendars?: string[]) => {
      void debouncedSearch(newSearchQuery, hiddenCalendars);
    };
  }, []);
  const resetSearch = useCallback(() => {
    setSearchQuery("");
    setSearchPersons([]);
    setShowTopCollaborators(false);
  }, []);

  useEffect(() => {
    if (!data) return;
    const valueFromData = compact(
      getValue(data?.viewer?.personQueryResultErrorable, "PersonQueryResult")?.persons || [],
    );
    setSearchPersons(valueFromData);
  }, [data]);

  return useMemo(() => {
    return {
      searchPersons,
      handlePersonSearch,
      resetSearch,
      setShowEmptyState,
      showTopCollaborators,
    };
  }, [searchPersons, handlePersonSearch, resetSearch, setShowEmptyState, showTopCollaborators]);
};
